"use client";

import { Eye, EyeClosed } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRouter, useSearchParams } from "next/navigation";
import { ToastTheme } from "@/utils/constants";
import SafeLink from "../SafeLink";
import { goApiFetch } from "@/lib/goApiClient";

type SignUpFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  acceptTerms: boolean;
};

type ApiError = {
  error: string;
};

type ApiResponse = {
  message: string;
  data: {
    user_id: string;
    email: string;
    name: {
      first: string;
      last: string;
    };
    token?: string;
    emailVerificationRequired?: boolean;
  };
};

// Set by the public PDF→QR converter on the marketing site when a visitor
// generates a menu before registering. Passing it to /v1/auth/signup makes
// the backend claim that already-built preview menu for the new account
// instead of creating an empty business. Read from the query string first
// (the CTA on the preview menu appends it), falling back to localStorage for
// visitors who navigate to sign-up some other way.
const CONVERTER_SESSION_STORAGE_KEY = "converter_session_id";

export function SignUpForm() {
  const t = useTranslations("components.sign_up");
  const router = useRouter();
  const searchParams = useSearchParams();
  const [converterSessionId, setConverterSessionId] = useState("");

  useEffect(() => {
    const fromQuery = searchParams.get("converter");
    if (fromQuery) {
      setConverterSessionId(fromQuery);
      try {
        localStorage.setItem(CONVERTER_SESSION_STORAGE_KEY, fromQuery);
      } catch {
        // Private browsing — the query param alone is enough for this visit.
      }
      return;
    }
    try {
      const stored = localStorage.getItem(CONVERTER_SESSION_STORAGE_KEY);
      if (stored) setConverterSessionId(stored);
    } catch {
      // Ignore: signing up without a converted menu is the normal path.
    }
  }, [searchParams]);

  const [formData, setFormData] = useState<SignUpFormData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    acceptTerms: false,
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    terms: "",
    general: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const toggleShowPassword = () => setShowPassword(!showPassword);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): boolean => {
    return password.length >= 8;
  };

  const validateForm = (data: SignUpFormData = formData): boolean => {
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      terms: "",
      general: "",
    };

    const trimmedFirstName = data.firstName.trim();
    const trimmedLastName = data.lastName.trim();
    const trimmedEmail = data.email.trim();

    if (!trimmedFirstName) {
      newErrors.firstName = t("error_first_name_required");
    }

    if (!trimmedLastName) {
      newErrors.lastName = t("error_last_name_required");
    }

    if (!trimmedEmail) {
      newErrors.email = t("error_email_is_required");
    } else if (!validateEmail(trimmedEmail)) {
      newErrors.email = t("error_enter_invalid_email");
    }

    if (!data.password) {
      newErrors.password = t("error_password_is_required");
    } else if (!validatePassword(data.password)) {
      newErrors.password = t("error_password_long");
    }

    if (!data.acceptTerms) {
      newErrors.terms = t("error_terms_required");
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleInputChange = (
    field: keyof SignUpFormData,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const sanitizedData: SignUpFormData = {
      ...formData,
      firstName: formData.firstName.trim().replace(/\s+/g, " "),
      lastName: formData.lastName.trim().replace(/\s+/g, " "),
      email: formData.email.trim(),
    };
    setFormData(sanitizedData);

    if (!validateForm(sanitizedData)) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await goApiFetch("/v1/auth/signup", {
        method: "POST",
        body: {
          firstName: sanitizedData.firstName,
          lastName: sanitizedData.lastName,
          email: sanitizedData.email.toLowerCase(),
          password: formData.password,
          acceptedKvkk: formData.acceptTerms,
          ...(converterSessionId ? { converterSessionId } : {}),
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorData = data as ApiError;
        setErrors((prev) => ({
          ...prev,
          general: errorData.error || "Registration failed",
        }));
        toast.error(errorData.error || "Registration failed", ToastTheme);
        return;
      }

      const successData = data as ApiResponse;

      // The session is single-use — a second signup in the same browser must
      // not try to reclaim a menu that now belongs to someone else.
      try {
        localStorage.removeItem(CONVERTER_SESSION_STORAGE_KEY);
      } catch {
        // Nothing to clean up.
      }

      // Check if email verification is required
      if (successData.data.emailVerificationRequired) {
        // Store email for verification pending page
        if (typeof window !== "undefined") {
          localStorage.setItem(
            "pending_verification_email",
            successData.data.email,
          );
        }

        toast.success(t("registration_successful_verify"), ToastTheme);

        // Redirect to verification pending page
        router.push("/verify-pending");
      } else {
        // Legacy flow - store token if provided
        if (successData.data.token && typeof window !== "undefined") {
          localStorage.setItem("auth_token", successData.data.token);
          localStorage.setItem(
            "user_data",
            JSON.stringify({
              user_id: successData.data.user_id,
              email: successData.data.email,
              name: successData.data.name,
            }),
          );

          // Set token as cookie via API for middleware access
          fetch("/api/auth/set-token", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: successData.data.token }),
          }).catch((err) => console.error("Failed to set token cookie:", err));
        }

        toast.success(t("registration_successful"), ToastTheme);

        // Redirect to dashboard
        router.push("/dashboard");
      }
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage = t("network_error");
      setErrors((prev) => ({
        ...prev,
        general: errorMessage,
      }));
      toast.error(errorMessage, ToastTheme);
    } finally {
      setIsLoading(false);
    }
  };

  const AlreadyHaveAccount = t.rich("already_have_account", {
    sign_in: (chunks) => (
      <SafeLink
        href="/sign-in"
        className="text-h-400 ms-1 font-semibold underline"
      >
        {chunks}
      </SafeLink>
    ),
  });

  const PrivacyCheckBoxText = t.rich("privacy_policy", {
    kvkk: (chunks) => (
      <SafeLink
        href="/kvkk"
        className="text-md font-semibold text-[#0C4974] not-italic underline"
        rel="noopener noreferrer"
        target="_blank"
      >
        {chunks}
      </SafeLink>
    ),
  });

  return (
    <form
      onSubmit={handleSubmit}
      className="flex grow flex-col items-start justify-center gap-8 rounded-lg p-8"
      aria-label="Sign Up Form"
    >
      {/* Headers */}
      <div className="w-full">
        <h2 className="text-ht-brown mb-2 text-3xl font-semibold">
          {t("title")}
        </h2>
        <p>{AlreadyHaveAccount}</p>
      </div>

      {/* General Error */}
      {errors.general && (
        <div className="w-full rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{errors.general}</p>
        </div>
      )}

      {/* Inputs */}
      <div className="w-full space-y-4">
        {/* Name Fields */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label
              htmlFor="firstName"
              className="block font-semibold text-gray-600"
            >
              {t("label_first_name")}
            </label>
            <input
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              onBlur={() =>
                handleInputChange(
                  "firstName",
                  formData.firstName.trim().replace(/\s+/g, " "),
                )
              }
              required
              placeholder={t("placeholder_first_name")}
              className={`w-full rounded border-b border-b-[#E6997B] py-2 leading-tight focus:outline-none ${
                errors.firstName ? "border border-red-500" : ""
              } text-ht-brown text-sm font-semibold placeholder-gray-400`}
            />
            {errors.firstName && (
              <p className="mt-1 text-xs text-red-500 italic">
                {errors.firstName}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="block font-semibold text-gray-600"
            >
              {t("label_last_name")}
            </label>
            <input
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              onBlur={() =>
                handleInputChange(
                  "lastName",
                  formData.lastName.trim().replace(/\s+/g, " "),
                )
              }
              required
              placeholder={t("placeholder_last_name")}
              className={`w-full rounded border-b border-b-[#E6997B] py-2 leading-tight focus:outline-none ${
                errors.lastName ? "border border-red-500" : ""
              } text-ht-brown text-sm font-semibold placeholder-gray-400`}
            />
            {errors.lastName && (
              <p className="mt-1 text-xs text-red-500 italic">
                {errors.lastName}
              </p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block font-semibold text-gray-600">
            {t("label_email_title")}
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            onBlur={() => handleInputChange("email", formData.email.trim())}
            required
            placeholder={t("placeholder_email")}
            className={`w-full rounded border-b border-b-[#E6997B] py-2 leading-tight focus:outline-none ${
              errors.email ? "border border-red-500" : ""
            } text-ht-brown text-sm font-semibold placeholder-gray-400`}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-500 italic">{errors.email}</p>
          )}
        </div>

        {/* Password */}
        <div className="flex flex-col">
          <label
            htmlFor="password"
            className="block font-semibold text-gray-600"
          >
            {t("label_password_title")}
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              required
              placeholder={t("placeholder_password")}
              className={`w-full rounded border-b border-b-[#E6997B] py-2 leading-tight focus:outline-none ${
                errors.password ? "border border-red-500" : ""
              } text-ht-brown text-sm font-semibold placeholder-gray-400`}
            />
            <button
              type="button"
              onClick={toggleShowPassword}
              className="absolute end-0 top-2 bottom-0"
            >
              {showPassword ? (
                <Eye className="h-5 w-5 text-gray-500" />
              ) : (
                <EyeClosed className="h-5 w-5 text-gray-500" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-xs text-red-500 italic">
              {errors.password}
            </p>
          )}

          {/* Terms Checkbox */}
          <div className="flex flex-row items-start gap-2 py-4">
            <input
              type="checkbox"
              id="terms"
              checked={formData.acceptTerms}
              onChange={(e) =>
                handleInputChange("acceptTerms", e.target.checked)
              }
              className="mt-0.5 size-4 checked:border-2 checked:border-slate-700"
            />
            <label htmlFor="terms" className="text-xs italic">
              {PrivacyCheckBoxText}
            </label>
          </div>
          {errors.terms && (
            <p className="text-xs text-red-500 italic">{errors.terms}</p>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="focus:shadow-outline bg-h-500 hover:bg-h-600 disabled:bg-h-300 w-full max-w-44 cursor-pointer self-end rounded-md px-6 py-3 font-bold text-white transition-colors focus:outline-none disabled:cursor-not-allowed"
          >
            {isLoading ? t("creating_account") : t("title")}
          </button>
        </div>
      </div>

      {/* Buttons */}
      {/* <div className="flex w-full flex-col items-end gap-4 md:items-center">
        <div className="relative mt-4 flex w-full max-w-88 justify-center self-center text-sm text-gray-500 md:mt-0 md:self-auto">
          <span className="z-10 bg-white px-2 text-center">
            {t("or_login_w_with_google")}
          </span>
          <div className="absolute start-0 end-0 top-3 w-full border-t border-t-gray-500"></div>
        </div>

        <button
          type="button"
          className="gsi-material-button self-center md:self-auto"
          aria-label="Sign in with Google"
          disabled={isLoading}
        >
          <div className="gsi-material-button-state"></div>
          <div className="gsi-material-button-content-wrapper">
            <div className="gsi-material-button-icon">
              <svg
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 48 48"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                style={{ display: "block" }}
              >
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                ></path>
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                ></path>
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                ></path>
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                ></path>
                <path fill="none" d="M0 0h48v48H0z"></path>
              </svg>
            </div>
            <span className="hidden">Sign in with Google</span>
          </div>
        </button>
      </div> */}
    </form>
  );
}
